import { DateRangePreset } from '../types/reports';
import { Timestamp } from 'firebase/firestore';

// Helper functions to get start and end of a day, week, month.
// These are simplified versions. Consider using a library like date-fns for robust handling.

export const getStartOfDay = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

export const getEndOfDay = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

export const getStartOfWeek = (date: Date, weekStartsOn: 0 | 1 = 1): Date => { // 0 for Sunday, 1 for Monday
  const newDate = new Date(date);
  const day = newDate.getDay();
  const diff = (day < weekStartsOn ? day + 7 : day) - weekStartsOn;
  newDate.setDate(newDate.getDate() - diff);
  return getStartOfDay(newDate);
};

export const getEndOfWeek = (date: Date, weekStartsOn: 0 | 1 = 1): Date => {
  const startOfWeek = getStartOfWeek(date, weekStartsOn);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  return getEndOfDay(endOfWeek);
};

export const getStartOfMonth = (date: Date): Date => {
  const newDate = new Date(date.getFullYear(), date.getMonth(), 1);
  return getStartOfDay(newDate);
};

export const getEndOfMonth = (date: Date): Date => {
  const newDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return getEndOfDay(newDate);
};

export const subDays = (date: Date, amount: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - amount);
  return newDate;
};

export const subWeeks = (date: Date, amount: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - amount * 7);
  return newDate;
};

export const subMonths = (date: Date, amount: number): Date => {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() - amount);
  return newDate;
};

export const getDateRange = (
  preset: DateRangePreset,
  customStart?: Date | null,
  customEnd?: Date | null
): { startDate: Date; endDate: Date } => {
  const today = new Date();
  let startDate: Date;
  let endDate: Date;

  switch (preset) {
    case DateRangePreset.TODAY:
      startDate = getStartOfDay(today);
      endDate = getEndOfDay(today);
      break;
    case DateRangePreset.YESTERDAY:
      const yesterday = subDays(today, 1);
      startDate = getStartOfDay(yesterday);
      endDate = getEndOfDay(yesterday);
      break;
    case DateRangePreset.THIS_WEEK:
      startDate = getStartOfWeek(today);
      endDate = getEndOfDay(today); // Or getEndOfWeek(today) if you want full week even if it's future
      break;
    case DateRangePreset.LAST_WEEK:
      const startOfLastWeek = getStartOfWeek(subWeeks(today, 1));
      startDate = startOfLastWeek;
      endDate = getEndOfWeek(startOfLastWeek);
      break;
    case DateRangePreset.THIS_MONTH:
      startDate = getStartOfMonth(today);
      endDate = getEndOfDay(today); // Or getEndOfMonth(today)
      break;
    case DateRangePreset.LAST_MONTH:
      const startOfLastMonth = getStartOfMonth(subMonths(today, 1));
      startDate = startOfLastMonth;
      endDate = getEndOfMonth(startOfLastMonth);
      break;
    case DateRangePreset.CUSTOM:
      if (customStart && customEnd) {
        startDate = getStartOfDay(customStart);
        endDate = getEndOfDay(customEnd);
      } else if (customStart) {
        startDate = getStartOfDay(customStart);
        endDate = getEndOfDay(customStart); // Default to single day if only start is provided
      } else {
        // Default to today if custom range is not fully specified
        startDate = getStartOfDay(today);
        endDate = getEndOfDay(today);
      }
      break;
    default:
      startDate = getStartOfDay(today);
      endDate = getEndOfDay(today);
  }
  return { startDate, endDate };
};

/**
 * Safely converts Firestore Timestamp or any date-like value to a JavaScript Date
 * @param timestamp - Value to convert (Firestore Timestamp, Date, or other)
 * @param fallbackToNow - Whether to fallback to current date (true) or undefined (false) if conversion fails
 * @returns Date object or undefined
 */
export const toDateSafe = (timestamp: any, fallbackToNow: boolean = true): Date | undefined => {
  // If it's a Firestore Timestamp with toDate method
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // If it's already a Date
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // Fallback behavior based on preference
  if (fallbackToNow) {
    console.warn("toDateSafe received an invalid timestamp:", timestamp, "Defaulting to current date.");
    return new Date();
  }
  
  return undefined;
};

/**
 * Safely converts a value to a Firestore Timestamp
 * @param value - Value to convert (Date, Timestamp, or other)
 * @returns Firestore Timestamp
 */
export const toTimestampSafe = (value: any): Timestamp => {
  // If already a Timestamp
  if (value instanceof Timestamp) {
    return value;
  }
  
  // If it's a Date
  if (value instanceof Date) {
    return Timestamp.fromDate(value);
  }
  
  // Fallback
  console.warn("toTimestampSafe received an invalid value:", value, "Defaulting to current timestamp.");
  return Timestamp.now();
};